import { useEffect } from "react";
import {
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import MicIcon from "@mui/icons-material/Mic";
import {
  useVoiceInput,
  VoiceLanguage,
} from "../hooks/useVoiceInput";

type Props = {
  onTranscript: (text: string) => void;
  language?: VoiceLanguage;
  className?: string;
};

/**
 * Mic button that records audio and delivers transcribed text via onTranscript.
 */
export default function VoiceInputButton({
  onTranscript,
  language = "en",
  className,
}: Props) {
  const {
    isRecording,
    isProcessing,
    transcript,
    error,
    start,
    stop,
    reset,
  } = useVoiceInput(language);

  useEffect(() => {
    if (!transcript) return;
    onTranscript(transcript);
    reset();
  }, [transcript, onTranscript, reset]);

  const handleClick = () => {
    if (isProcessing) return;
    if (isRecording) {
      stop();
    } else {
      start();
    }
  };

  const tooltip = isProcessing
    ? "Transcribing…"
    : isRecording
      ? "Stop recording"
      : "Voice input";

  return (
    <Box className={className} sx={{ display: "flex", flexDirection: "column" }}>
      <Tooltip title={tooltip}>
        <span>
          <IconButton
            onClick={handleClick}
            disabled={isProcessing}
            color={isRecording ? "error" : "default"}
            aria-label={tooltip}
            sx={{
              position: "relative",
              ...(isRecording && {
                animation: "voicePulse 1.5s ease-out infinite",
                "@keyframes voicePulse": {
                  "0%": { boxShadow: "0 0 0 0 rgba(211, 47, 47, 0.5)" },
                  "70%": { boxShadow: "0 0 0 10px rgba(211, 47, 47, 0)" },
                  "100%": { boxShadow: "0 0 0 0 rgba(211, 47, 47, 0)" },
                },
              }),
            }}
          >
            {isProcessing ? (
              <CircularProgress size={24} />
            ) : (
              <MicIcon />
            )}
          </IconButton>
        </span>
      </Tooltip>
      {error && (
        <Typography variant="caption" color="error" sx={{ maxWidth: 140 }}>
          {error}
        </Typography>
      )}
    </Box>
  );
}
